import { LegalPage, usePageMeta, Placeholder } from "../components/Legal";
import { BRAND } from "../data/siteData";

const SECTIONS = [
    {
        heading: "Introduction and Acceptance of Terms",
        body: [
            "These Internet Service Terms & Conditions (“Terms”) govern the broadband, TV and OTT services provided by Eway Fiber Net (“we”, “our”, “us”) in Narasaraopet, Andhra Pradesh, India. By applying for, activating or using an Eway Fiber Net connection, you agree to these Terms.",
            "Nothing in these Terms limits any right you have under applicable Indian law or telecom regulations that cannot be limited by contract.",
        ],
    },
    {
        heading: "Eligibility and Subscriber Registration",
        body: [
            "Connections are provided to residents and businesses within our service area, subject to technical feasibility at your location. You agree to provide accurate registration details — including your name, mobile number and service address — and to keep them up to date. Identity or address documents may be requested where required by applicable regulation.",
        ],
    },
    {
        heading: "Broadband Plans, Speeds and Service Descriptions",
        body: [
            "Current standard plans are published on our Plans page: 30 Mbps at ₹350/month, 50 Mbps at ₹590/month, 75 Mbps at ₹750/month and 100 Mbps at ₹1,000/month, with 6-month options available with or without a router as listed on the website.",
            "Plan speeds are maximum FTTH line speeds. Actual experienced speed can vary with network load, Wi-Fi conditions, your devices and the destination of your traffic. TV channel and OTT benefits are provided as described in the offers active at the time of subscription.",
        ],
    },
    {
        heading: "Plan Prices, Taxes and Applicable Charges",
        body: [
            "Prices are as published on our website at the time of subscription or renewal. An installation charge applies where published (currently ₹600 on the 30 Mbps plan). Applicable taxes, if any, are charged as required by law. Any change in pricing will be reflected on the website and applies from your next subscription or renewal.",
        ],
    },
    {
        heading: "Billing, Payments and Renewals",
        body: [
            "Subscriptions are billed in advance for the chosen period (monthly or 6-month plans). Please renew before your due date to avoid interruption of service. Payments can be made through our official Paytm bill-payment page, via UPI apps, or with assistance from customer care.",
            "We never ask for your UPI PIN, card details, passwords or OTPs on calls or messages. Only enter payment details on the secure pages of your chosen payment provider.",
        ],
    },
    {
        heading: "Installation and Equipment",
        body: [
            "Installation is carried out by our team or authorized field partners. Where you select a “with router” option, the router arrangement is as communicated at the time of subscription.",
            <>
                Ownership, warranty and replacement terms for provider-supplied
                equipment:{" "}
                <Placeholder>
                    To be confirmed — Eway Fiber Net to specify router ownership and
                    warranty terms before publishing
                </Placeholder>
            </>,
        ],
    },
    {
        heading: "Customer Responsibilities and Account Security",
        body: [
            "You are responsible for activity on your connection and for keeping your subscriber-portal credentials and Wi-Fi password secure. Please inform us promptly of any unauthorized use of your account or a change in your contact details or address.",
        ],
    },
    {
        heading: "Acceptable Use of Internet Services",
        body: [
            "Your connection is intended for lawful personal, household or business use consistent with your plan. You agree to use the service in a way that does not harm our network, other customers, or third parties.",
        ],
    },
    {
        heading: "Prohibited Activities and Unlawful Use",
        body: [
            "You must not use the service for any unlawful purpose, including transmitting illegal content, fraud, spam, hacking, distributing malware, infringing intellectual-property rights, or reselling/redistributing the connection to others without our written consent. Suspected unlawful use may be reported to the appropriate authorities.",
        ],
    },
    {
        heading: "Network Maintenance, Interruptions and Service Availability",
        body: [
            "We work to keep the network highly available, and our published uptime goal is 99.9%. Planned maintenance is carried out with advance notice where practical. Occasional interruptions may occur due to faults, fiber cuts, power issues or events beyond our reasonable control; in such cases our team works to restore service as quickly as possible.",
        ],
    },
    {
        heading: "Fair-Use Policies and Traffic Management",
        body: [
            <>
                No separate fair-usage cap is currently published for our standard
                plans.{" "}
                <Placeholder>
                    To be confirmed — Eway Fiber Net to state whether any fair-use
                    policy or traffic-management rule applies before publishing
                </Placeholder>
            </>,
        ],
    },
    {
        heading: "Complaints, Support and Fault Reporting",
        body: [
            `For faults, complaints or service requests, contact customer care on ${BRAND.phones[0].raw} or ${BRAND.phones[1].raw}, between ${BRAND.careHours}. You can also reach us on WhatsApp. Please keep your subscriber details ready so we can locate your account quickly.`,
        ],
    },
    {
        heading: "Suspension and Termination of Services",
        body: [
            "We may suspend or terminate a connection for non-payment after the due date, misuse of the service, unlawful use, or a material breach of these Terms — with notice where appropriate. You may request disconnection of your service by contacting customer care.",
        ],
    },
    {
        heading: "Refunds, Cancellations and Applicable Conditions",
        body: [
            <>
                Our refund and cancellation conditions:{" "}
                <Placeholder>
                    To be confirmed — Eway Fiber Net to approve the refund /
                    cancellation policy (e.g., treatment of unused plan periods)
                    before publishing
                </Placeholder>
                {" "}Your statutory rights under applicable law remain unaffected.
            </>,
        ],
    },
    {
        heading: "Equipment Return and Damage",
        body: [
            <>
                Where provider-supplied equipment is installed, conditions for its
                return and liability for damage:{" "}
                <Placeholder>
                    To be confirmed — Eway Fiber Net to specify equipment return and
                    damage terms where applicable
                </Placeholder>
            </>,
        ],
    },
    {
        heading: "Service Changes and Plan Revisions",
        body: [
            "We may revise plans, prices or service features from time to time. Changes will be published on our website and apply to new subscriptions and renewals; your active paid period is not changed mid-cycle.",
        ],
    },
    {
        heading: "Limitation of Liability",
        body: [
            "To the extent permitted by applicable law, Eway Fiber Net is not liable for indirect or consequential losses arising from service interruptions or factors beyond our reasonable control. Nothing in these Terms excludes or limits liability that cannot be excluded or limited under Indian law or applicable telecom regulations.",
        ],
    },
    {
        heading: "Privacy and Data Protection",
        body: [
            "Your personal information is handled as described in our Privacy Policy, available on this website. By using our services you acknowledge that policy.",
        ],
    },
    {
        heading: "Changes to These Terms",
        body: [
            "We may update these Terms from time to time. The current version will always be published on this page with a revised “last updated” date. Continued use of the service after an update constitutes acceptance of the revised Terms.",
        ],
    },
    {
        heading: "Customer Support and Grievance Contact",
        body: [
            `Eway Fiber Net, ${BRAND.location}. Customer care: ${BRAND.phones[0].raw} / ${BRAND.phones[1].raw} (${BRAND.careHours}).`,
            <>
                Designated grievance officer details:{" "}
                <Placeholder>
                    To be updated — Eway Fiber Net to confirm the grievance officer
                    name and contact before publishing
                </Placeholder>
            </>,
        ],
    },
];

export default function Terms() {
    usePageMeta(
        "Internet Service Terms & Conditions — Eway Fiber Net",
        "Terms and conditions for Eway Fiber Net FTTH broadband subscribers in Narasaraopet, Andhra Pradesh — plans, billing, installation, acceptable use, support and more."
    );
    return (
        <LegalPage
            testId="terms-page"
            eyebrow="LEGAL & POLICIES"
            title={
                <>
                    Internet Service{" "}
                    <span className="text-gradient-fire">Terms & Conditions</span>
                </>
            }
            subtitle="The terms that govern Eway Fiber Net broadband, TV and OTT services for residential and business subscribers in Narasaraopet, Andhra Pradesh."
            updated="[DATE TO BE CONFIRMED BY EWAY FIBER NET]"
            sections={SECTIONS}
        />
    );
}
